import { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { format } from 'date-fns';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { converterApi } from '@services/api';
import type { Conversion, ConversionDetails } from '@types';

interface ConversionDetailsModalProps {
  isOpen: boolean;
  conversion: Conversion | null;
  onClose: () => void;
  onDownload: (conversion: Conversion) => void;
  onRetry: (conversion: Conversion) => void;
  onDelete: (conversion: Conversion) => void;
}

export function ConversionDetailsModal({
  isOpen,
  conversion,
  onClose,
  onDownload,
  onRetry,
  onDelete,
}: ConversionDetailsModalProps) {
  const [details, setDetails] = useState<ConversionDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetails = useCallback(async () => {
    if (!conversion) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await converterApi.getConversionDetails(conversion.id);
      setDetails(data);
    } catch (err) {
      setError('Failed to load conversion details');
      console.error('Error fetching details:', err);
      // Fallback to basic conversion data
      setDetails(conversion as ConversionDetails);
    } finally {
      setIsLoading(false);
    }
  }, [conversion]);

  useEffect(() => {
    if (isOpen && conversion) {
      fetchDetails();
    }
    return () => {
      setDetails(null);
      setError(null);
    };
  }, [isOpen, conversion, fetchDetails]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="h-6 w-6 text-green-600" />;
      case 'FAILED':
        return <XCircleIcon className="h-6 w-6 text-red-600" />;
      case 'PROCESSING':
        return <ClockIcon className="h-6 w-6 text-blue-600 animate-pulse" />;
      case 'PENDING':
        return <ClockIcon className="h-6 w-6 text-yellow-600" />;
      default:
        return <ExclamationTriangleIcon className="h-6 w-6 text-gray-600" />;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 ring-green-600/20';
      case 'FAILED':
        return 'bg-red-100 text-red-800 ring-red-600/20';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 ring-yellow-600/20';
      case 'PROCESSING':
        return 'bg-blue-100 text-blue-800 ring-blue-600/20';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800 ring-gray-600/20';
      default:
        return 'bg-gray-100 text-gray-800 ring-gray-600/20';
    }
  };

  const formatBytes = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (!conversion) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(conversion.status)}
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-gray-900"
                      >
                        Conversion Details
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        ID: {conversion.id}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                  ) : error ? (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  ) : (
                    <Tab.Group>
                      <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-4">
                        <Tab
                          className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors ${
                              selected
                                ? 'bg-white text-blue-700 shadow'
                                : 'text-gray-700 hover:bg-white/50 hover:text-gray-900'
                            }`
                          }
                        >
                          Overview
                        </Tab>
                        <Tab
                          className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors ${
                              selected
                                ? 'bg-white text-blue-700 shadow'
                                : 'text-gray-700 hover:bg-white/50 hover:text-gray-900'
                            }`
                          }
                        >
                          Validation Results
                        </Tab>
                        <Tab
                          className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors ${
                              selected
                                ? 'bg-white text-blue-700 shadow'
                                : 'text-gray-700 hover:bg-white/50 hover:text-gray-900'
                            }`
                          }
                        >
                          Transformation
                        </Tab>
                        <Tab
                          className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors ${
                              selected
                                ? 'bg-white text-blue-700 shadow'
                                : 'text-gray-700 hover:bg-white/50 hover:text-gray-900'
                            }`
                          }
                        >
                          Timeline
                        </Tab>
                      </Tab.List>
                      <Tab.Panels>
                        {/* Overview Tab */}
                        <Tab.Panel>
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-xs font-medium text-gray-500 uppercase">
                                  Status
                                </label>
                                <div className="mt-1">
                                  <span
                                    className={`inline-flex items-center rounded-md px-2 py-1 text-sm font-medium ring-1 ring-inset ${getStatusBadgeClass(
                                      conversion.status
                                    )}`}
                                  >
                                    {conversion.status}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500 uppercase">
                                  Source System
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {conversion.source_system}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500 uppercase">
                                  Filename
                                </label>
                                <p
                                  className="mt-1 text-sm text-gray-900 truncate"
                                  title={conversion.file_name}
                                >
                                  {conversion.file_name}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500 uppercase">
                                  File Size
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {details?.file_size_human ||
                                    formatBytes(details?.file_size)}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500 uppercase">
                                  Records
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {conversion.record_count ??
                                    conversion.estimated_records ??
                                    'N/A'}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500 uppercase">
                                  Processing Time
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {conversion.processing_time_seconds
                                    ? `${conversion.processing_time_seconds}s`
                                    : 'N/A'}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500 uppercase">
                                  Created At
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {format(
                                    new Date(conversion.created_at),
                                    'MMM d, yyyy h:mm a'
                                  )}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500 uppercase">
                                  Completed At
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {conversion.completed_at
                                    ? format(
                                        new Date(conversion.completed_at),
                                        'MMM d, yyyy h:mm a'
                                      )
                                    : 'N/A'}
                                </p>
                              </div>
                            </div>
                            {conversion.error_message && (
                              <div className="rounded-md bg-red-50 p-4">
                                <div className="flex">
                                  <XCircleIcon className="h-5 w-5 text-red-400" />
                                  <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">
                                      Error Message
                                    </h3>
                                    <p className="mt-1 text-sm text-red-700">
                                      {conversion.error_message}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </Tab.Panel>

                        {/* Validation Results Tab */}
                        <Tab.Panel>
                          {details?.validation_results ? (
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="rounded-lg bg-red-50 p-4">
                                  <div className="text-sm font-medium text-red-900">
                                    Total Errors
                                  </div>
                                  <div className="mt-1 text-2xl font-bold text-red-600">
                                    {details.validation_results.total_errors}
                                  </div>
                                </div>
                                <div className="rounded-lg bg-yellow-50 p-4">
                                  <div className="text-sm font-medium text-yellow-900">
                                    Total Warnings
                                  </div>
                                  <div className="mt-1 text-2xl font-bold text-yellow-600">
                                    {details.validation_results.total_warnings}
                                  </div>
                                </div>
                              </div>
                              {details.validation_results.errors.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                                    Errors
                                  </h4>
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {details.validation_results.errors.map(
                                      (error, idx) => (
                                        <div
                                          key={idx}
                                          className="rounded-md bg-red-50 p-3 text-sm text-red-800"
                                        >
                                          <strong>{error.field}:</strong>{' '}
                                          {error.message}
                                          {error.row_number &&
                                            ` (Row ${error.row_number})`}
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                              {details.validation_results.warnings.length >
                                0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                                    Warnings
                                  </h4>
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {details.validation_results.warnings.map(
                                      (warning, idx) => (
                                        <div
                                          key={idx}
                                          className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800"
                                        >
                                          <strong>{warning.field}:</strong>{' '}
                                          {warning.message}
                                          {warning.row_number &&
                                            ` (Row ${warning.row_number})`}
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 text-center py-8">
                              No validation results available
                            </p>
                          )}
                        </Tab.Panel>

                        {/* Transformation Tab */}
                        <Tab.Panel>
                          {details?.transformation_results ? (
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="rounded-lg bg-blue-50 p-4">
                                  <div className="text-sm font-medium text-blue-900">
                                    Fields Mapped
                                  </div>
                                  <div className="mt-1 text-2xl font-bold text-blue-600">
                                    {
                                      details.transformation_results
                                        .fields_mapped
                                    }
                                  </div>
                                </div>
                                <div className="rounded-lg bg-green-50 p-4">
                                  <div className="text-sm font-medium text-green-900">
                                    Records Processed
                                  </div>
                                  <div className="mt-1 text-2xl font-bold text-green-600">
                                    {
                                      details.transformation_results
                                        .records_processed
                                    }
                                  </div>
                                </div>
                              </div>
                              {details.transformation_results.transformations &&
                                details.transformation_results.transformations
                                  .length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                                      Applied Transformations
                                    </h4>
                                    <div className="max-h-64 overflow-y-auto">
                                      <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                              Field
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                              Original
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                              Transformed
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                              Rule
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                          {details.transformation_results.transformations.map(
                                            (t, idx) => (
                                              <tr key={idx}>
                                                <td className="px-3 py-2 text-sm text-gray-900">
                                                  {t.field}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-600">
                                                  {t.original_value}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-900">
                                                  {t.transformed_value}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500">
                                                  {t.transformation_applied}
                                                </td>
                                              </tr>
                                            )
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 text-center py-8">
                              No transformation data available
                            </p>
                          )}
                        </Tab.Panel>

                        {/* Timeline Tab */}
                        <Tab.Panel>
                          <div className="flow-root">
                            <ul className="-mb-8">
                              {details?.processing_timeline?.uploaded_at && (
                                <li>
                                  <div className="relative pb-8">
                                    <span
                                      className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                      aria-hidden="true"
                                    />
                                    <div className="relative flex space-x-3">
                                      <div>
                                        <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                                          <ClockIcon className="h-5 w-5 text-white" />
                                        </span>
                                      </div>
                                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                        <div>
                                          <p className="text-sm text-gray-900">
                                            File Uploaded
                                          </p>
                                        </div>
                                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                          {format(
                                            new Date(
                                              details.processing_timeline
                                                .uploaded_at
                                            ),
                                            'h:mm:ss a'
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              )}
                              {details?.processing_timeline
                                ?.started_processing_at && (
                                <li>
                                  <div className="relative pb-8">
                                    <span
                                      className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                      aria-hidden="true"
                                    />
                                    <div className="relative flex space-x-3">
                                      <div>
                                        <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                                          <ClockIcon className="h-5 w-5 text-white" />
                                        </span>
                                      </div>
                                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                        <div>
                                          <p className="text-sm text-gray-900">
                                            Processing Started
                                          </p>
                                        </div>
                                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                          {format(
                                            new Date(
                                              details.processing_timeline
                                                .started_processing_at
                                            ),
                                            'h:mm:ss a'
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              )}
                              {details?.processing_timeline
                                ?.validation_completed_at && (
                                <li>
                                  <div className="relative pb-8">
                                    <span
                                      className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                      aria-hidden="true"
                                    />
                                    <div className="relative flex space-x-3">
                                      <div>
                                        <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                                          <CheckCircleIcon className="h-5 w-5 text-white" />
                                        </span>
                                      </div>
                                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                        <div>
                                          <p className="text-sm text-gray-900">
                                            Validation Completed
                                          </p>
                                        </div>
                                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                          {format(
                                            new Date(
                                              details.processing_timeline
                                                .validation_completed_at
                                            ),
                                            'h:mm:ss a'
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              )}
                              {details?.processing_timeline
                                ?.transformation_completed_at && (
                                <li>
                                  <div className="relative pb-8">
                                    <span
                                      className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                      aria-hidden="true"
                                    />
                                    <div className="relative flex space-x-3">
                                      <div>
                                        <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                                          <CheckCircleIcon className="h-5 w-5 text-white" />
                                        </span>
                                      </div>
                                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                        <div>
                                          <p className="text-sm text-gray-900">
                                            Transformation Completed
                                          </p>
                                        </div>
                                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                          {format(
                                            new Date(
                                              details.processing_timeline
                                                .transformation_completed_at
                                            ),
                                            'h:mm:ss a'
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              )}
                              {details?.completed_at && (
                                <li>
                                  <div className="relative pb-0">
                                    <div className="relative flex space-x-3">
                                      <div>
                                        <span
                                          className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                                            conversion.status === 'COMPLETED'
                                              ? 'bg-green-500'
                                              : 'bg-red-500'
                                          }`}
                                        >
                                          {conversion.status === 'COMPLETED' ? (
                                            <CheckCircleIcon className="h-5 w-5 text-white" />
                                          ) : (
                                            <XCircleIcon className="h-5 w-5 text-white" />
                                          )}
                                        </span>
                                      </div>
                                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                        <div>
                                          <p className="text-sm text-gray-900">
                                            {conversion.status === 'COMPLETED'
                                              ? 'Conversion Completed'
                                              : 'Conversion Failed'}
                                          </p>
                                        </div>
                                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                          {format(
                                            new Date(details.completed_at),
                                            'h:mm:ss a'
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              )}
                            </ul>
                          </div>
                        </Tab.Panel>
                      </Tab.Panels>
                    </Tab.Group>
                  )}
                </div>

                {/* Footer Actions */}
                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-gray-50">
                  {conversion.status === 'COMPLETED' &&
                    conversion.output_file_id && (
                      <button
                        onClick={() => {
                          onDownload(conversion);
                          onClose();
                        }}
                        className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        Download
                      </button>
                    )}
                  {conversion.status === 'FAILED' && (
                    <button
                      onClick={() => {
                        onRetry(conversion);
                        onClose();
                      }}
                      className="inline-flex items-center gap-2 rounded-md bg-yellow-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Retry
                    </button>
                  )}
                  <button
                    onClick={() => {
                      onDelete(conversion);
                      onClose();
                    }}
                    className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </button>
                  <button
                    onClick={onClose}
                    className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
